import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2, Circle, ArrowLeft, Square, Play, Pause, RotateCcw, Video,
} from 'lucide-react';
import DrillInfoModal from '@/components/coach-lab/DrillInfoModal';

const NAVY = '#0B1824';
const ORANGE = '#ffa133';
const MUTED = '#8FA3B1';
const BORDER = '#DDE5EC';

function fmtClock(secs) {
  const m = Math.floor(Math.max(0, secs) / 60);
  const s = Math.max(0, secs) % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function PracticeChecklist() {
  const { practiceId } = useParams();
  useOutletContext(); // ensure layout context is wired
  const navigate = useNavigate();
  const [practice, setPractice] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [drillMeta, setDrillMeta] = useState([]);   // [{ drill_id, name, section_type, duration_minutes, video_url, description }]
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [ending, setEnding] = useState(false);

  // Per-row timer state. key = idx
  const [timers, setTimers] = useState({}); // { idx: { remaining: secs, running: bool } }
  const tickRef = useRef(null);

  // Drill info modal (build 215). Holds the meta object for the active
  // drill row when the user taps its name — same shape used by the
  // Drill Library's `DrillInfoModal`, so the popdown is identical:
  // description, duration, category, difficulty, tags, plus a
  // "Watch in YouTube" external handoff if the drill has a video_url.
  const [activeDrill, setActiveDrill] = useState(null);

  /* ── Load practice + enrich drill metadata ── */
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/scheduled-practices');
        const found = (res.data.practices || []).find(p => p.practice_id === practiceId);
        if (!found) { toast.error('Practice not found'); navigate('/coach/lab'); return; }
        if (cancelled) return;
        setPractice(found);
        setChecklist(found.checklist || []);
        setStarted(found.status === 'in_progress' || found.status === 'completed');

        // Flatten section drills (the source of truth for drill_id + duration)
        const flat = [];
        (found.sections || []).forEach(sec => {
          (sec.drills || []).forEach(d => {
            flat.push({
              drill_id: d.drill_id || null,
              name: d.name,
              section_type: sec.section_type,
              duration_minutes: d.duration_minutes || 0,
              description: d.description || '',
              // Filled below from the drill-library lookup — keeps the
              // info modal at parity with the Drill Library popdown.
              video_url: null,
              category: d.category || null,
              difficulty: d.difficulty || null,
              tags: Array.isArray(d.tags) ? d.tags : [],
            });
          });
        });

        // Enrich with video_url + category/difficulty/tags via drill lookup
        const ids = [...new Set(flat.map(d => d.drill_id).filter(Boolean))];
        if (ids.length > 0) {
          try {
            // /drills?limit=200 — server doesn't have a /drills?ids= filter, so pull a generous page and match.
            const dr = await api.get('/drills', { params: { limit: 500 } });
            const lookup = {};
            (dr.data?.drills || []).forEach(d => { lookup[d.drill_id] = d; });
            flat.forEach(item => {
              const found2 = lookup[item.drill_id];
              if (found2) {
                item.video_url = (found2.video_url || '').trim() || null;
                if (!item.duration_minutes && found2.duration_minutes) item.duration_minutes = found2.duration_minutes;
                if (!item.description && found2.description) item.description = found2.description;
                if (!item.category && found2.category) item.category = found2.category;
                if (!item.difficulty && found2.difficulty) item.difficulty = found2.difficulty;
                if ((!item.tags || item.tags.length === 0) && Array.isArray(found2.tags)) item.tags = found2.tags;
              }
            });
          } catch { /* non-fatal */ }
        }
        if (!cancelled) setDrillMeta(flat);
      } catch { toast.error('Failed to load practice'); }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [practiceId, navigate]);

  /* ── Single global tick for any running timer ── */
  useEffect(() => {
    const anyRunning = Object.values(timers).some(t => t.running);
    if (!anyRunning) {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    if (!tickRef.current) {
      tickRef.current = setInterval(() => {
        setTimers(prev => {
          const next = { ...prev };
          let beeped = false;
          for (const k of Object.keys(next)) {
            if (next[k].running) {
              const r = Math.max(0, (next[k].remaining || 0) - 1);
              next[k] = { ...next[k], remaining: r, running: r > 0 };
              if (r === 0 && !beeped) beeped = true;
            }
          }
          if (beeped) toast.success('Drill timer finished');
          return next;
        });
      }, 1000);
    }
    return () => {
      if (tickRef.current && !Object.values(timers).some(t => t.running)) {
        clearInterval(tickRef.current); tickRef.current = null;
      }
    };
  }, [timers]);

  /* ── Helpers for drill row data (zip checklist + drillMeta) ── */
  const rows = useMemo(() => checklist.map((item, idx) => ({
    idx,
    completed: !!item.completed,
    drill_name: item.drill_name,
    section_type: item.section_type,
    meta: drillMeta[idx] || null,
  })), [checklist, drillMeta]);

  const total = rows.length;
  const completedCount = rows.filter(r => r.completed).length;
  const isCompleted = practice?.status === 'completed';

  /* ── Actions ── */
  const toggleItem = (idx) => {
    if (isCompleted) return;
    setChecklist(prev => prev.map((it, i) => i === idx ? { ...it, completed: !it.completed } : it));
  };

  const handleStart = async () => {
    try {
      await api.put(`/scheduled-practices/${practiceId}`, {
        status: 'in_progress',
        started_at: new Date().toISOString(),
      });
      setStarted(true);
      setPractice(prev => ({ ...prev, status: 'in_progress' }));
      toast.success('Practice started');
    } catch { toast.error('Failed to start'); }
  };

  const handleEnd = async () => {
    setEnding(true);
    try {
      await api.put(`/scheduled-practices/${practiceId}`, {
        status: 'completed',
        completed_at: new Date().toISOString(),
        checklist,
      });
      setPractice(prev => ({ ...prev, status: 'completed' }));
      toast.success('Practice completed');
    } catch { toast.error('Failed to save'); }
    finally { setEnding(false); }
  };

  const handleTimerToggle = (idx, fallbackMins) => {
    setTimers(prev => {
      const cur = prev[idx];
      if (!cur) {
        const total = (drillMeta[idx]?.duration_minutes || fallbackMins || 5) * 60;
        return { ...prev, [idx]: { remaining: total, running: true } };
      }
      if (cur.remaining === 0) {
        const total = (drillMeta[idx]?.duration_minutes || fallbackMins || 5) * 60;
        return { ...prev, [idx]: { remaining: total, running: true } };
      }
      return { ...prev, [idx]: { ...cur, running: !cur.running } };
    });
  };
  const handleTimerReset = (idx, fallbackMins) => {
    setTimers(prev => ({
      ...prev,
      [idx]: {
        remaining: (drillMeta[idx]?.duration_minutes || fallbackMins || 5) * 60,
        running: false,
      },
    }));
  };

  /* Open the drill info modal. Mirrors the Drill Library behaviour: the
     popdown always shows the drill metadata; if a YouTube video_url is
     present, the modal also renders a "Watch in YouTube" pill that
     hands off to SFSafariViewController / Custom Tabs (build 215). */
  const openDrillInfo = (meta) => {
    if (!meta) return;
    setActiveDrill(meta);
  };

  /* ── Render ── */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-10 w-10 border-4 border-slate-900 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  if (!practice) return null;

  return (
    <div className="max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/coach/lab')}
          className="p-1.5 rounded-full hover:bg-slate-200 transition-colors"
          style={{ background: 'none', border: 'none' }}
          data-testid="back-to-lab">
          <ArrowLeft className="w-5 h-5 text-slate-700" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-extrabold uppercase tracking-tight truncate"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", color: NAVY }}
              data-testid="checklist-title">
            {practice.plan_name}
          </h1>
          <p className="text-xs" style={{ color: MUTED }}>
            {practice.team_name}
            {practice.scheduled_date && ` · ${new Date(practice.scheduled_date + 'T00:00').toLocaleDateString()}`}
          </p>
        </div>
        {started && !isCompleted && (
          <Button
            onClick={handleEnd}
            disabled={ending}
            className="rounded-full bg-red-600 hover:bg-red-700 text-white text-[15px] font-semibold uppercase tracking-[.07em] gap-1.5 px-5 h-10 shrink-0"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            data-testid="end-practice-btn"
          >
            <Square className="w-3.5 h-3.5" />
            {ending ? 'Saving…' : 'End Practice'}
          </Button>
        )}
        {isCompleted && (
          <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider">Completed</span>
        )}
      </div>

      {/* Start Card */}
      {!started && (
        <div className="rounded-xl border-2 bg-white p-8 text-center mb-6"
             style={{ borderColor: BORDER }} data-testid="start-session-card">
          <h2 className="text-xl font-extrabold uppercase tracking-tight mb-2"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", color: NAVY }}>
            Ready to Run Practice?
          </h2>
          <p className="text-sm mb-5" style={{ color: MUTED }}>{total} drill{total !== 1 ? 's' : ''} in this plan</p>
          <Button
            onClick={handleStart}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[15px] font-semibold uppercase tracking-[.07em] gap-2 px-8 h-12"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            data-testid="start-session-btn"
          >
            Start Session
          </Button>
        </div>
      )}

      {/* Checklist */}
      {started && (
        <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: BORDER }} data-testid="practice-checklist">
          {rows.map((row, idx) => {
            const isFirst = idx === 0 || rows[idx - 1]?.section_type !== row.section_type;
            const meta = row.meta || {};
            const hasVideo = !!meta.video_url;
            const tState = timers[idx];
            const targetSecs = (meta.duration_minutes || 0) * 60;
            const remaining = tState ? tState.remaining : targetSecs;
            const running = !!tState?.running;
            return (
              <div key={idx}>
                {isFirst && (
                  <div className="px-4 py-2 bg-slate-50" style={{ borderBottom: `1px solid ${BORDER}` }}>
                    <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: MUTED }}>
                      {row.section_type?.replace(/_/g, ' ')}
                    </p>
                  </div>
                )}
                <div
                  className={`flex items-center gap-3 px-4 py-3 ${row.completed ? 'bg-emerald-50/50' : ''}`}
                  style={{ borderBottom: '1px solid #f1f5f9' }}
                  data-testid={`checklist-item-${idx}`}
                >
                  {/* Toggle complete */}
                  <button
                    onClick={() => toggleItem(idx)}
                    disabled={isCompleted}
                    className="shrink-0"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: isCompleted ? 'default' : 'pointer' }}
                    data-testid={`checklist-toggle-${idx}`}
                    aria-label={row.completed ? 'Mark incomplete' : 'Mark complete'}
                  >
                    {row.completed
                      ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      : <Circle className="w-5 h-5 text-slate-300" />}
                  </button>

                  {/* Drill name. Every row opens the info modal on tap
                      (build 215) — same popdown as the Drill Library,
                      showing description / duration / category /
                      difficulty / tags, plus a "Watch in YouTube"
                      external handoff when the drill has a video_url. */}
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => openDrillInfo(meta)}
                      data-testid={`drill-info-${idx}`}
                      className={`inline-flex items-center gap-1.5 text-left text-[14px] font-semibold ${row.completed ? 'line-through' : ''}`}
                      style={{
                        color: row.completed ? '#94a3b8' : NAVY,
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '.02em',
                      }}
                    >
                      {hasVideo && <Video className="w-3.5 h-3.5 shrink-0" style={{ color: ORANGE }} />}
                      <span className={hasVideo ? 'underline decoration-dotted underline-offset-2' : ''}>{row.drill_name}</span>
                    </button>
                    {meta.duration_minutes ? (
                      <p className="text-[10px] uppercase tracking-[.08em] mt-0.5" style={{ color: MUTED }}>
                        Target {meta.duration_minutes}m
                      </p>
                    ) : null}
                  </div>

                  {/* Inline timer */}
                  {!isCompleted && targetSecs > 0 && (
                    <div className="flex items-center gap-1 shrink-0" data-testid={`drill-timer-${idx}`}>
                      <button
                        onClick={() => handleTimerToggle(idx)}
                        className="h-8 min-w-[68px] px-2 rounded-full flex items-center gap-1 font-mono text-[12px] font-bold transition-colors"
                        style={{
                          background: running ? '#0B1824' : (remaining === 0 ? 'rgba(206,17,65,0.10)' : '#F1F5F9'),
                          color: running ? '#fff' : (remaining === 0 ? '#CE1141' : NAVY),
                          border: 'none',
                        }}
                        aria-label={running ? 'Pause timer' : 'Start timer'}
                      >
                        {running ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                        <span>{fmtClock(tState ? remaining : targetSecs)}</span>
                      </button>
                      {tState && (
                        <button
                          onClick={() => handleTimerReset(idx)}
                          className="h-8 w-8 rounded-full flex items-center justify-center transition-colors hover:bg-slate-100"
                          style={{ background: 'none', border: 'none', color: MUTED }}
                          aria-label="Reset timer"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Progress Footer */}
      {started && total > 0 && (
        <p className="text-center text-xs mt-4" style={{ color: MUTED }} data-testid="checklist-progress">
          {completedCount} of {total} drill{total !== 1 ? 's' : ''} completed
        </p>
      )}

      {/* Drill info modal (build 215) — shared with Drill Library. */}
      <DrillInfoModal drill={activeDrill} onOpenChange={(o) => { if (!o) setActiveDrill(null); }} />
    </div>
  );
}
